import React from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'bonsai_chat_session_id';
const LEAD_KEY = 'bonsai_chat_lead_captured';
const OPENED_KEY = 'bonsai_chat_opened_once';

function uuid() {
  return 'sess-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = uuid();
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

const INITIAL_MSG = {
  role: 'assistant',
  content: 'Ciao 👋 Sono **bonsAI**. Parlami dei tuoi problemi con i marketplace — listing, ordini, prezzi, resi, customer care, qualunque cosa ti faccia perdere tempo. Ti aiuto a capire come risolverlo.',
};

export default function AIChatWidget() {
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(false);
  const [messages, setMessages] = React.useState([INITIAL_MSG]);
  const [input, setInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [requireLead, setRequireLead] = React.useState(false);
  const [leadCaptured, setLeadCaptured] = React.useState(() => localStorage.getItem(LEAD_KEY) === '1');
  const [leadForm, setLeadForm] = React.useState({ name: '', email: '', phone: '', privacy: false });
  const [leadSubmitting, setLeadSubmitting] = React.useState(false);
  const [leadError, setLeadError] = React.useState('');
  const scrollerRef = React.useRef(null);
  const inputRef = React.useRef(null);

  // Hide widget on admin routes
  const isAdmin = location.pathname.startsWith('/admin');

  // Auto-open bubble preview after 15s (once per session)
  React.useEffect(() => {
    if (isAdmin || leadCaptured) return;
    if (sessionStorage.getItem(OPENED_KEY) === '1') return;
    const t = setTimeout(() => {
      if (!open) setShowPreview(true);
      sessionStorage.setItem(OPENED_KEY, '1');
    }, 15000);
    return () => clearTimeout(t);
  }, [isAdmin, leadCaptured, open]);

  // Auto-scroll messages
  React.useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, loading, requireLead]);

  // Focus input when opening
  React.useEffect(() => {
    if (open && inputRef.current && !requireLead) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open, requireLead]);

  const openChat = () => {
    setShowPreview(false);
    setOpen(true);
  };

  const closeChat = () => setOpen(false);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    if (requireLead) return; // Block while gate is active
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ai-chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: getSessionId(),
          message: text,
          page_source: location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Errore');
      setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
      if (data.require_lead && !leadCaptured) {
        setTimeout(() => setRequireLead(true), 400);
      }
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: 'Ops, un attimo di respiro — riprova tra qualche secondo 🌱' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setLeadError('');
    const name = leadForm.name.trim();
    const email = leadForm.email.trim();
    const phone = leadForm.phone.trim();
    if (!name || !email || !phone) {
      setLeadError('Compila tutti i campi.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLeadError('Email non valida.');
      return;
    }
    if (!leadForm.privacy) {
      setLeadError('Devi accettare la privacy policy.');
      return;
    }
    setLeadSubmitting(true);
    try {
      const res = await fetch(`${API}/api/ai-chat/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: getSessionId(),
          name,
          email,
          phone,
          page_source: location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Errore invio');
      localStorage.setItem(LEAD_KEY, '1');
      setLeadCaptured(true);
      setRequireLead(false);
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: `Perfetto ${name.split(' ')[0]}, grazie! Ti richiamiamo entro 24h lavorative. Nel frattempo dimmi pure altro o prenota una demo cliccando qui sotto 👇`,
          cta: true,
        },
      ]);
    } catch (err) {
      setLeadError(err.message || 'Errore invio, riprova.');
    } finally {
      setLeadSubmitting(false);
    }
  };

  if (isAdmin) return null;

  return (
    <>
      {/* Preview bubble (auto-shown after 15s) */}
      {showPreview && !open && (
        <div className="bonsai-chat-preview" data-testid="chat-preview-bubble" onClick={openChat} role="button" tabIndex={0}>
          <button
            type="button"
            className="bonsai-chat-preview-close"
            aria-label="Chiudi anteprima"
            onClick={(e) => { e.stopPropagation(); setShowPreview(false); }}
          >×</button>
          <p className="bonsai-chat-preview-title">Parla dei tuoi problemi con i marketplace a <span className="bonsai-brand">bons<span className="bonsai-brand-ai">AI</span></span></p>
          <p className="bonsai-chat-preview-text">Raccontamelo in 30 secondi, ti dico come Bonsai può risolverlo.</p>
        </div>
      )}

      {/* Floating trigger button */}
      {!open && (
        <button
          type="button"
          className="bonsai-chat-fab"
          data-testid="chat-fab"
          onClick={openChat}
          aria-label="Parla con bonsAI"
        >
          <i className="bi bi-chat-dots-fill" aria-hidden="true"></i>
          <span className="bonsai-chat-fab-label">Parla con <span className="bonsai-brand">bons<span className="bonsai-brand-ai">AI</span></span></span>
          <span className="bonsai-chat-fab-dot" aria-hidden="true"></span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="bonsai-chat-panel" role="dialog" aria-label="Parla con bonsAI" data-testid="chat-panel">
          <div className="bonsai-chat-header">
            <div className="bonsai-chat-header-avatar" aria-hidden="true">
              <i className="bi bi-robot"></i>
            </div>
            <div className="bonsai-chat-header-info">
              <div className="bonsai-chat-header-title"><span className="bonsai-brand">bons<span className="bonsai-brand-ai">AI</span></span></div>
              <div className="bonsai-chat-header-sub"><span className="bonsai-chat-online-dot"></span>Online — risposta in 5s</div>
            </div>
            <button type="button" className="bonsai-chat-close" onClick={closeChat} aria-label="Chiudi chat" data-testid="chat-close">
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          <div className="bonsai-chat-messages" ref={scrollerRef} data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`bonsai-msg bonsai-msg--${m.role}`} data-testid={`chat-msg-${m.role}-${i}`}>
                {m.role === 'assistant' && <div className="bonsai-msg-avatar"><i className="bi bi-robot"></i></div>}
                <div className="bonsai-msg-bubble">
                  <div className="bonsai-msg-text" dangerouslySetInnerHTML={{ __html: formatMarkdown(m.content) }} />
                  {m.cta && (
                    <a
                      href="/contatti"
                      className="bonsai-msg-cta"
                      data-testid="chat-cta-demo"
                    >Prenota una demo →</a>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="bonsai-msg bonsai-msg--assistant" data-testid="chat-typing">
                <div className="bonsai-msg-avatar"><i className="bi bi-robot"></i></div>
                <div className="bonsai-msg-bubble">
                  <div className="bonsai-typing"><span></span><span></span><span></span></div>
                </div>
              </div>
            )}

            {requireLead && !leadCaptured && (
              <div className="bonsai-lead-gate" data-testid="chat-lead-gate">
                <div className="bonsai-lead-gate-title">
                  <i className="bi bi-shield-check"></i> Prima di continuare
                </div>
                <p className="bonsai-lead-gate-desc">
                  Per darti una risposta personalizzata e mandarti una demo su misura, lasciami i tuoi contatti.
                </p>
                <form onSubmit={submitLead} className="bonsai-lead-form">
                  <input
                    type="text"
                    placeholder="Il tuo nome"
                    value={leadForm.name}
                    onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                    data-testid="chat-lead-name"
                    aria-label="Il tuo nome"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email lavorativa"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                    data-testid="chat-lead-email"
                    aria-label="Email lavorativa"
                    required
                  />
                  <input
                    type="tel"
                    placeholder="Telefono"
                    value={leadForm.phone}
                    onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                    data-testid="chat-lead-phone"
                    aria-label="Telefono"
                    required
                  />
                  <label className="bonsai-lead-privacy">
                    <input
                      type="checkbox"
                      checked={leadForm.privacy}
                      onChange={(e) => setLeadForm({ ...leadForm, privacy: e.target.checked })}
                      data-testid="chat-lead-privacy"
                    />
                    <span>Accetto la <a href="/privacy" target="_blank" rel="noopener noreferrer">privacy policy</a></span>
                  </label>
                  {leadError && <div className="bonsai-lead-error" data-testid="chat-lead-error">{leadError}</div>}
                  <button type="submit" disabled={leadSubmitting} data-testid="chat-lead-submit">
                    {leadSubmitting ? 'Invio…' : 'Continua la conversazione'}
                  </button>
                </form>
              </div>
            )}
          </div>

          {!requireLead && (
            <form
              className="bonsai-chat-input"
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
            >
              <input
                ref={inputRef}
                type="text"
                placeholder="Scrivi il tuo problema…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={loading}
                data-testid="chat-input"
                aria-label="Scrivi un messaggio"
              />
              <button type="submit" disabled={loading || !input.trim()} data-testid="chat-send" aria-label="Invia messaggio">
                <i className="bi bi-send-fill"></i>
              </button>
            </form>
          )}

          <div className="bonsai-chat-footer">
            <span>Powered by AI · <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy</a></span>
          </div>
        </div>
      )}
    </>
  );
}

// Simple markdown-lite for bot messages: **bold**
function formatMarkdown(text) {
  const escaped = String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');
}
